/**
 * /board/$boardId — board with view switcher (Kanban / List / Gantt).
 *
 * The active view is resolved in this order:
 *   1. `?view=` query param (link sharable)
 *   2. user_view_preferences row (server-side persisted)
 *   3. default → 'kanban'
 *
 * Selecting a view does two things:
 *   - mirrors the choice into the URL (so reload + share works)
 *   - upserts user_view_preferences via /api/views/* (so the user's
 *     next visit lands on the same view even without the query param)
 *
 * The Kanban path is unchanged from Phase 2 (we hand off to
 * <KanbanBoard />). List and Gantt mount the chaos-ui native
 * components built in Phase 12 — see ../../../components/views/.
 */
import type { ViewType } from '@chaos/types/views';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { KanbanBoard } from '../../../components/kanban/kanban-board';
import { ServerLoadMonitor } from '../../../components/layout/server-load';
import { GanttView } from '../../../components/views/gantt';
import { ListView } from '../../../components/views/list';
import { ViewSwitcher } from '../../../components/views/view-switcher';
import { bulkApi, dependenciesApi, dependenciesKeys } from '../../../lib/api';
import { kanbanApi, kanbanKeys } from '../../../lib/api/kanban';
import { viewsApi, viewsKeys } from '../../../lib/api/views';

const viewSearchSchema = z.object({
  view: z.enum(['kanban', 'list', 'gantt']).optional(),
});

function BoardPage() {
  const { t } = useTranslation();
  const { boardId } = useParams({ strict: false }) as { boardId: string };
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { view?: ViewType };
  const qc = useQueryClient();

  const boardQ = useQuery({
    queryKey: kanbanKeys.board(boardId),
    queryFn: () => kanbanApi.getBoard(boardId),
  });

  const prefQ = useQuery({
    queryKey: viewsKeys.preference(boardId),
    queryFn: () => viewsApi.getPreference(boardId),
    staleTime: 60_000,
  });

  // Effective view: ?view= → persisted → kanban
  const view: ViewType = search.view ?? prefQ.data?.viewType ?? 'kanban';

  const upsertView = useMutation({
    mutationFn: (next: ViewType) => viewsApi.upsertPreference(boardId, { viewType: next }),
    onSuccess: (preference) => {
      qc.setQueryData(viewsKeys.preference(boardId), preference);
    },
  });

  function setView(next: ViewType) {
    void navigate({
      to: '/board/$boardId',
      params: { boardId },
      search: (prev) => ({ ...(prev as object), view: next }),
      replace: true,
    });
    if (prefQ.data?.viewType !== next) {
      upsertView.mutate(next);
    }
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 px-6 pt-3 pb-2">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
              {boardQ.data?.name ?? t('common.loading')}
            </h1>
            <ViewSwitcher value={view} onChange={setView} />
          </div>
          <ServerLoadMonitor />
        </header>
        <div className="flex-1 overflow-hidden px-6 pb-6">
          {view === 'kanban' ? (
            <div className="h-full">
              <KanbanBoard boardId={boardId} />
            </div>
          ) : view === 'list' ? (
            <BoardListView boardId={boardId} />
          ) : (
            <BoardGanttView boardId={boardId} />
          )}
        </div>
      </div>

      {/* Card-detail panel renders here when route is /board/:id/card/:id */}
      <Outlet />
    </div>
  );
}

// ── Embedded List ───────────────────────────────────────────────────
function BoardListView({ boardId }: { boardId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const cardsQ = useQuery({
    queryKey: kanbanKeys.boardCards(boardId),
    queryFn: () => kanbanApi.listCards(boardId),
  });

  const archiveMutation = useMutation({
    mutationFn: (cardIds: string[]) => bulkApi.run({ action: 'archive', cardIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
    },
  });

  function openCard(cardId: string) {
    void navigate({
      to: '/board/$boardId/card/$cardId',
      params: { boardId, cardId },
    });
  }

  return (
    <ListView
      cards={cardsQ.data ?? []}
      isLoading={cardsQ.isLoading}
      onOpenCard={openCard}
      onBulkArchive={(ids) => archiveMutation.mutate(ids)}
    />
  );
}

// ── Embedded Gantt ──────────────────────────────────────────────────
function BoardGanttView({ boardId }: { boardId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const cardsQ = useQuery({
    queryKey: kanbanKeys.boardCards(boardId),
    queryFn: () => kanbanApi.listCards(boardId),
  });
  const depsQ = useQuery({
    queryKey: dependenciesKeys.forBoard(boardId),
    queryFn: () => dependenciesApi.list(boardId),
    staleTime: 30_000,
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({ cardId, dueDate }: { cardId: string; dueDate: string }) =>
      kanbanApi.updateCard(cardId, { dueDate }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
    },
  });

  function openCard(cardId: string) {
    void navigate({
      to: '/board/$boardId/card/$cardId',
      params: { boardId, cardId },
    });
  }

  return (
    <GanttView
      cards={cardsQ.data ?? []}
      dependencies={depsQ.data ?? []}
      isLoading={cardsQ.isLoading || depsQ.isLoading}
      onOpenCard={openCard}
      onReschedule={(cardId, dueDate) => rescheduleMutation.mutate({ cardId, dueDate })}
    />
  );
}

export const Route = createFileRoute('/_authenticated/board/$boardId')({
  validateSearch: viewSearchSchema,
  component: BoardPage,
});
